/* COC Outreach: native Shopify forms are the baseline; JS adds progressive enhancements. */
(() => {
  'use strict';
  const configNode = document.getElementById('ThemeConfig');
  const config = configNode ? JSON.parse(configNode.textContent) : {routes:{},messages:{}};
  const routes = config.routes;
  const announcer = document.querySelector('[data-announcer]');
  const variantRequests = new WeakMap();
  let cartQueue = Promise.resolve();
  let predictionController;
  let searchTimer;
  let toastTimer;
  const dropdownMotions = new WeakMap();
  const dialogMotions = new WeakMap();
  const hoverTimers = new WeakMap();
  const hoverOpened = new WeakSet();
  const motionEnabled = () => config.animations !== false && !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const motionOptions = {duration:Math.min(600,Math.max(160,Number(config.motionDuration) || 360)),easing:'cubic-bezier(.22,1,.36,1)'};
  function setDropdown(details, expanded) {
    const previous = dropdownMotions.get(details);
    details.dataset.menuExpanded = String(expanded);
    if (!expanded) hoverOpened.delete(details);
    if (previous?.expanded === expanded) return;
    if (!previous && details.open === expanded) return;
    const panel = details.querySelector(':scope > .nav-dropdown__content, :scope > .mobile-submenu');
    if (!panel || !panel.animate || !motionEnabled()) { previous?.animation.cancel(); panel?.style.removeProperty('overflow'); dropdownMotions.delete(details); details.open = expanded; return; }
    const wasOpen = details.open;
    const current = getComputedStyle(panel);
    const mobile = panel.classList.contains('mobile-submenu');
    const start = {opacity:wasOpen ? current.opacity : '0'};
    if (mobile) start.height = wasOpen ? `${panel.getBoundingClientRect().height}px` : '0px';
    else start.transform = wasOpen ? current.transform : 'translateY(-10px) scale(.985)';
    previous?.animation.cancel();
    details.open = true;
    const end = {opacity:expanded ? '1' : '0'};
    if (mobile) { end.height = expanded ? `${panel.scrollHeight}px` : '0px'; panel.style.overflow = 'hidden'; }
    else end.transform = expanded ? 'translateY(0) scale(1)' : 'translateY(-6px) scale(.99)';
    const animation = panel.animate([start,end],{...motionOptions,duration:motionOptions.duration * (previous ? .7 : expanded ? 1 : .8)});
    dropdownMotions.set(details,{animation,expanded});
    animation.finished.then(() => {
      if (dropdownMotions.get(details)?.animation !== animation) return;
      details.open = expanded;
      panel.style.removeProperty('overflow');
      dropdownMotions.delete(details);
    }).catch(() => {});
  }
  const hoverMenusEnabled = () => config.hoverMenus !== false && window.matchMedia?.('(hover: hover) and (pointer: fine)').matches;
  document.addEventListener('pointerover',event => {
    const details = event.target.closest('.desktop-nav .nav-dropdown');
    if (!details || details.contains(event.relatedTarget) || !hoverMenusEnabled()) return;
    clearTimeout(hoverTimers.get(details));
    if (!details.open || dropdownMotions.get(details)?.expanded === false) hoverOpened.add(details);
    document.querySelectorAll('.desktop-nav details[open]').forEach(other => { if (other !== details) setDropdown(other,false); });
    setDropdown(details,true);
  });
  document.addEventListener('pointerout',event => {
    const details = event.target.closest('.desktop-nav .nav-dropdown');
    if (!details || details.contains(event.relatedTarget) || !hoverMenusEnabled()) return;
    clearTimeout(hoverTimers.get(details));
    hoverTimers.set(details,setTimeout(() => {
      hoverTimers.delete(details);
      if (!details.contains(document.activeElement)) setDropdown(details,false);
    },160));
  });
  document.addEventListener('toggle',event => {
    if (event.target.matches?.('.nav-dropdown,.mobile-menu nav details') && !dropdownMotions.has(event.target)) event.target.dataset.menuExpanded = String(event.target.open);
  },true);
  function closeDialog(dialog) {
    if (!dialog?.open || dialog.dataset.closing) return;
    dialogMotions.get(dialog)?.cancel();
    if (!dialog.animate || !motionEnabled()) { dialog.close(); return; }
    const drawer = dialog.matches('.mobile-menu,.cart-dialog');
    dialog.dataset.closing = 'true';
    const animation = dialog.animate([{opacity:1,transform:'none'},{opacity:0,transform:drawer ? 'none' : 'translateY(12px)'}],motionOptions);
    dialogMotions.set(dialog,animation);
    animation.finished.then(() => { dialog.close(); }).catch(() => {}).finally(() => {
      delete dialog.dataset.closing;
      if (dialogMotions.get(dialog) === animation) dialogMotions.delete(dialog);
    });
  }
  const announce = message => { if (announcer) announcer.textContent = message; };
  const notify = message => {
    let toast = document.querySelector('.toast');
    if (!toast) { toast = document.createElement('div'); toast.className = 'toast'; toast.setAttribute('role','status'); document.body.append(toast); }
    toast.textContent = message; toast.hidden = false;
    clearTimeout(toastTimer); toastTimer = setTimeout(() => { toast.hidden = true; }, 6000);
    announce(message);
  };
  const errorText = error => error?.description || error?.message || config.messages.error || 'Please try again.';
  async function jsonRequest(url, options = {}) {
    const response = await fetch(url, {credentials:'same-origin', ...options});
    let payload;
    try { payload = await response.json(); } catch (_) { throw new Error(config.messages.error); }
    if (!response.ok) throw new Error(errorText(payload));
    return payload;
  }
  const apiURL = base => base.endsWith('.js') ? base : `${base}.js`;
  function openDialog(dialog) {
    if (!dialog || dialog.open) return;
    dialog.showModal();
    if (dialog.animate && motionEnabled()) {
      const drawer = dialog.matches('.mobile-menu,.cart-dialog');
      const animation = dialog.animate([{opacity:0,transform:drawer ? 'none' : 'translateY(12px)'},{opacity:1,transform:'none'}],motionOptions);
      dialogMotions.set(dialog,animation);
      animation.finished.then(() => { if (dialogMotions.get(dialog) === animation) dialogMotions.delete(dialog); }).catch(() => {});
    }
    dialog.querySelectorAll('iframe[data-video-src]').forEach(frame => { frame.src = frame.dataset.videoSrc; });
    const field = dialog.querySelector('input:not([type=hidden])');
    if (field) field.focus();
  }
  document.addEventListener('close', event => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement)) return;
    dialog.querySelectorAll('iframe[data-video-src]').forEach(frame => { frame.removeAttribute('src'); });
    dialog.querySelectorAll('video').forEach(video => video.pause());
  }, true);
  document.addEventListener('cancel',event => {
    if (event.target instanceof HTMLDialogElement) { event.preventDefault(); closeDialog(event.target); }
  },true);
  document.addEventListener('keydown',event => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('.desktop-nav details[open]').forEach(details => { setDropdown(details,false); details.querySelector('summary')?.focus(); });
  });
  async function refreshCart() {
    const main = document.querySelector('[data-main-cart]');
    const sectionIds = ['cart-drawer'];
    if (main) sectionIds.push(main.dataset.sectionId);
    const url = new URL(routes.cart, window.location.origin);
    url.searchParams.set('sections',sectionIds.join(','));
    const [sections,cart] = await Promise.all([
      jsonRequest(url.toString()), jsonRequest(apiURL(routes.cart))
    ]);
    const parse = html => new DOMParser().parseFromString(html, 'text/html');
    if (sections['cart-drawer']) {
      const fresh = parse(sections['cart-drawer']).querySelector('[data-cart-fragment]');
      const current = document.querySelector('[data-cart-fragment]');
      if (fresh && current) current.innerHTML = fresh.innerHTML;
    }
    if (main && sections[main.dataset.sectionId]) {
      const fresh = parse(sections[main.dataset.sectionId]).querySelector('[data-main-cart]');
      if (fresh) main.replaceWith(fresh);
    }
    document.querySelectorAll('[data-cart-count]').forEach(count => { count.textContent = cart.item_count; count.hidden = cart.item_count === 0; });
    document.dispatchEvent(new CustomEvent('cart:updated',{detail:{cart}}));
    return cart;
  }
  function updateCart(form, removedKey) {
    const updates = {};
    form?.querySelectorAll('[data-cart-quantity]').forEach(input => {
      const amount = Number(input.value);
      if (!Number.isInteger(amount) || amount < 0) throw new Error(config.messages.error);
      updates[input.dataset.lineKey] = amount;
    });
    if (removedKey) updates[removedKey] = 0;
    const payload = {updates};
    const note = form?.querySelector('[name=note]');
    if (note) payload.note = note.value;
    cartQueue = cartQueue.then(async () => {
      const focusedId = document.activeElement?.id;
      try {
        await jsonRequest(apiURL(routes.update), {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)});
        await refreshCart();
        if (focusedId) document.getElementById(focusedId)?.focus();
        announce(config.messages.updated);
      } catch (error) { notify(errorText(error)); }
    });
    return cartQueue;
  }
  document.addEventListener('click', async event => {
    const summary = event.target.closest('summary');
    const dropdown = summary?.parentElement;
    if (dropdown?.matches('.nav-dropdown, .mobile-menu nav details') && ((dropdown.querySelector('.nav-dropdown__content,.mobile-submenu')?.animate && motionEnabled()) || hoverOpened.has(dropdown))) {
      event.preventDefault();
      const expanded = hoverOpened.has(dropdown) || !(dropdownMotions.get(dropdown)?.expanded ?? dropdown.open);
      hoverOpened.delete(dropdown);
      document.querySelectorAll('.desktop-nav details[open]').forEach(other => { if (other !== dropdown) setDropdown(other,false); });
      setDropdown(dropdown,expanded);
    }
    document.querySelectorAll('.desktop-nav details[open]').forEach(details => { if (!details.contains(event.target)) setDropdown(details,false); });
    const target = event.target.closest('a,button,dialog');
    if (!target) return;
    if (target.matches('[data-open-search]')) { event.preventDefault(); openDialog(document.querySelector('[data-search-dialog]')); }
    if (target.matches('[data-open-menu]')) { event.preventDefault(); openDialog(document.querySelector('[data-menu-dialog]')); }
    if (target.matches('[data-close-dialog]')) { event.preventDefault(); closeDialog(target.closest('dialog')); }
    if (target.matches('[data-open-dialog]')) { event.preventDefault(); openDialog(document.getElementById(target.dataset.openDialog)); }
    if (target.matches('[data-open-cart]') && config.cartBehavior === 'drawer') {
      event.preventDefault();
      try { await refreshCart(); openDialog(document.querySelector('[data-cart-dialog]')); }
      catch (_) { window.location.assign(target.href); }
    }
    if (target.matches('dialog')) {
      const rect = target.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeDialog(target);
    }
    if (target.matches('[data-cart-remove]')) {
      event.preventDefault();
      try { await updateCart(target.closest('form'),target.dataset.lineKey); } catch (error) { notify(errorText(error)); }
    }
    if (target.matches('[data-media-target]')) {
      const gallery = target.closest('.product-gallery');
      gallery.querySelectorAll('[data-media-panel]').forEach(panel => {
        panel.hidden = panel.dataset.mediaPanel !== target.dataset.mediaTarget;
        if (panel.hidden) panel.querySelectorAll('video').forEach(video => video.pause());
      });
      gallery.querySelectorAll('[data-media-target]').forEach(button => button.setAttribute('aria-pressed',String(button === target)));
    }
    if (target.matches('[data-copy-link]')) {
      try {
        await navigator.clipboard.writeText(window.location.href);
        notify(config.messages.copied);
      } catch (_) {
        const field = document.createElement('textarea'); field.value = window.location.href;
        document.body.append(field); field.select();
        const copied = document.execCommand('copy'); field.remove();
        if (copied) notify(config.messages.copied); else notify(window.location.href);
      }
    }
  });
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (form.matches('[data-search-prefix]')) {
      const field = form.querySelector('[name=q]');
      const prefix = form.dataset.searchPrefix;
      if (prefix && field.value.trim() && !field.value.startsWith(prefix)) field.value = `${prefix} ${field.value}`;
    }
    if (form.matches('[data-cart-form]')) {
      if (event.submitter?.name === 'checkout') return;
      event.preventDefault();
      try { await updateCart(form); } catch (error) { notify(errorText(error)); }
    }
    if (!form.matches('[data-product-form]') || config.cartBehavior !== 'drawer') return;
    if (event.submitter && event.submitter.name !== 'add') return;
    event.preventDefault();
    const button = event.submitter || form.querySelector('[name=add]');
    const errorNode = form.querySelector('[data-form-error]');
    if (errorNode) errorNode.hidden = true;
    if (button) { button.disabled = true; button.setAttribute('aria-busy','true'); }
    try {
      const requestData = new FormData(form);
      await jsonRequest(apiURL(routes.add),{method:'POST',body:requestData,headers:{Accept:'application/json'}});
      try { await refreshCart(); openDialog(document.querySelector('[data-cart-dialog]')); }
      catch (_) { window.location.assign(routes.cart); }
      announce(config.messages.added);
    } catch (error) {
      if (errorNode) { errorNode.textContent = errorText(error); errorNode.hidden = false; }
      else notify(errorText(error));
    } finally {
      if (button) { button.disabled = false; button.removeAttribute('aria-busy'); }
    }
  });
  document.addEventListener('change', async event => {
    const input = event.target;
    if (input.matches('[data-auto-submit]')) input.form?.requestSubmit();
    if (input.matches('[data-cart-quantity]')) {
      if (!input.checkValidity()) { input.reportValidity(); return; }
      try { await updateCart(input.closest('form')); } catch (error) { notify(errorText(error)); }
    }
    if (!input.matches('[data-variant-select]')) return;
    const section = input.closest('[data-product-section]');
    const selectedId = Number(input.value);
    const variants = JSON.parse(section.querySelector('[data-variant-data]').textContent);
    const variant = variants.find(item => item.id === selectedId);
    if (!variant) return;
    const hiddenId = section.querySelector('[data-variant-id]');
    if (hiddenId) { hiddenId.value = selectedId; hiddenId.disabled = false; }
    const oldPrice = section.querySelector('[data-product-price]');
    if (oldPrice) oldPrice.textContent = variant.price;
    const oldQuantity = section.querySelector('[name=quantity]')?.value;
    if (variant.mediaId) section.querySelector(`[data-media-target="${variant.mediaId}"]`)?.click();
    const location = new URL(window.location.href); location.searchParams.set('variant',selectedId);
    window.history.replaceState({},'',location);
    variantRequests.get(section)?.abort();
    const controller = new AbortController(); variantRequests.set(section,controller);
    const url = new URL(section.dataset.productUrl,window.location.origin);
    url.searchParams.set('variant',selectedId); url.searchParams.set('section_id',section.dataset.sectionId);
    try {
      const response = await fetch(url,{signal:controller.signal,credentials:'same-origin'});
      if (!response.ok) throw new Error(config.messages.error);
      const documentFragment = new DOMParser().parseFromString(await response.text(),'text/html');
      const fresh = documentFragment.querySelector('[data-product-info]');
      if (!fresh) throw new Error(config.messages.error);
      const info = section.querySelector('[data-product-info]'); info.innerHTML = fresh.innerHTML;
      initProductForms(section);
      const quantity = info.querySelector('[name=quantity]');
      if (quantity && oldQuantity && Number(oldQuantity) >= Number(quantity.min || 1) && (!quantity.max || Number(oldQuantity) <= Number(quantity.max))) quantity.value = oldQuantity;
      info.querySelector('[data-variant-select]')?.focus();
      if (window.Shopify?.PaymentButton) window.Shopify.PaymentButton.init();
      document.dispatchEvent(new CustomEvent('coc:product:updated',{detail:{variant,section}}));
    } catch (error) { if (error.name !== 'AbortError') notify(errorText(error)); }
  });
  document.addEventListener('input',event => {
    const input = event.target;
    if (!input.matches('[data-predictive-input]') || !config.predictiveSearch) return;
    clearTimeout(searchTimer); predictionController?.abort();
    const region = document.querySelector('[data-predictive-results]');
    if (!region) return;
    const term = input.value.trim();
    if (term.length < 2) { region.replaceChildren(); region.removeAttribute('aria-busy'); return; }
    searchTimer = setTimeout(async () => {
      predictionController = new AbortController(); region.setAttribute('aria-busy','true');
      const url = new URL(routes.predictive,window.location.origin);
      url.searchParams.set('q',term); url.searchParams.set('resources[type]','product,article,page,collection');
      url.searchParams.set('resources[limit]','6'); url.searchParams.set('section_id','predictive-search');
      try {
        const response = await fetch(url,{signal:predictionController.signal,credentials:'same-origin'});
        if (!response.ok) throw new Error();
        const parsed = new DOMParser().parseFromString(await response.text(),'text/html');
        const results = parsed.querySelector('.predictive-results');
        region.innerHTML = results ? results.outerHTML : '';
      } catch (error) { if (error.name !== 'AbortError') region.replaceChildren(); }
      finally { region.removeAttribute('aria-busy'); }
    },250);
  });
  function initArticle(root) {
    root.querySelectorAll('[data-article-section]').forEach(section => {
      if (!section.dataset.formatted) {
        section.dataset.formatted = 'true';
        if (section.dataset.formatHeadings === 'true') section.querySelectorAll('[data-article-body] p').forEach(paragraph => {
          const text = paragraph.textContent.trim();
          if (text.length < 160 && /^(\d+\.\s|A Story:|Final Thought:)/i.test(text)) {
            const heading = document.createElement('h2');
            while (paragraph.firstChild) heading.append(paragraph.firstChild);
            paragraph.replaceWith(heading);
          }
        });
        if (section.dataset.formatScriptures === 'true') section.querySelectorAll('[data-article-body] p').forEach(reference => {
          const quote = reference.nextElementSibling;
          if (/^(?:[1-3]\s)?[A-Za-z]+\s+\d+:\d+.*\([A-Z]+\)$/.test(reference.textContent.trim()) && quote?.tagName === 'P' && /^[“"]/.test(quote.textContent.trim())) {
            const blockquote = document.createElement('blockquote');
            reference.before(blockquote); reference.classList.add('meta');
            blockquote.append(reference,quote);
          }
        });
      }
      const list = section.querySelector('[data-toc-list]');
      if (!list || list.dataset.ready) return;
      list.dataset.ready = 'true';
      section.querySelectorAll('[data-article-body] h2,[data-article-body] h3').forEach((heading,index) => {
        if (!heading.id) heading.id = `article-section-${index + 1}`;
        const item = document.createElement('li'); const link = document.createElement('a');
        link.href = `#${heading.id}`; link.textContent = heading.textContent; item.append(link); list.append(item);
      });
      if (!list.children.length) section.querySelector('[data-toc]')?.setAttribute('hidden','');
    });
  }
  function initRecommendations(root) {
    root.querySelectorAll('[data-recommendations][data-url]').forEach(section => {
      if (section.dataset.initialized) return; section.dataset.initialized = 'true';
      const load = async () => {
        try {
          const response = await fetch(section.dataset.url,{credentials:'same-origin'});
          if (!response.ok) return;
          const documentFragment = new DOMParser().parseFromString(await response.text(),'text/html');
          const fresh = documentFragment.querySelector('[data-recommendations]');
          if (fresh?.querySelector('.product-card')) section.innerHTML = fresh.innerHTML;
        } catch (_) { /* The manual/fallback collection stays visible. */ }
      };
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) { observer.disconnect(); load(); } },{rootMargin:'300px'});
        observer.observe(section);
      } else load();
    });
  }
  function initProductForms(root) {
    const sections = root.matches?.('[data-product-section]') ? [root] : root.querySelectorAll('[data-product-section]');
    sections.forEach(section => {
      const select = section.querySelector('[data-variant-select]');
      const hidden = section.querySelector('[data-variant-id]');
      if (select && hidden) { hidden.value = select.value; hidden.disabled = false; select.removeAttribute('name'); }
    });
  }
  function init(root = document) {
    initProductForms(root); initArticle(root); initRecommendations(root);
    // Display a plain-language query while retaining collection-scoped product searches.
    const search = root.querySelector('#MainSearch');
    if (search) {
      const match = search.value.match(/^(product_type:"[^"]+")\s+([\s\S]+)$/);
      if (match) { search.form.dataset.searchPrefix = match[1]; search.value = match[2]; }
    }
  }
  init();
  document.addEventListener('shopify:section:load',event => init(event.target));
  document.addEventListener('shopify:block:select',event => {
    const parent = event.target.closest('details'); if (parent) parent.open = true;
  });
})();
